import { stickerUrl } from "@listou/shared/stickers";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import {
  CHART_AGES,
  chartCell,
  predictGender,
  type GenderPrediction,
} from "../../lib/chinese-calendar";
import { addDays, addMonths } from "../../lib/pregnancy";
import { ISO_DAY } from "./due-date";
import { ToolLayout, type ToolCopy } from "./tool-layout";

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
const AGES = Array.from(
  { length: CHART_AGES.max - CHART_AGES.min + 1 },
  (_, index) => CHART_AGES.min + index,
);

const TONE = {
  boy: { label: "Garçon", cell: "bg-[#cfe3f4]", text: "text-[#1d5f8f]" },
  girl: { label: "Fille", cell: "bg-[#fbd5df]", text: "text-accent-text" },
};

export const CHINESE_CALENDAR_COPY: ToolCopy = {
  h1: "Calendrier chinois de grossesse : fille ou garçon ?",
  intro:
    "Indiquez votre date de naissance et la date de conception : le calcul convertit les deux dans le calendrier lunaire chinois et lit la case du tableau traditionnel. Un jeu, pas un diagnostic.",
  sections: [
    {
      heading: "D'où vient ce tableau ?",
      paragraphs: [
        "La légende le fait remonter à un tombeau royal près de Pékin, il y a plusieurs siècles. Aucune source ne l'atteste, mais le tableau circule depuis des décennies et reste l'une des prédictions les plus consultées par les futurs parents.",
        "Il croise deux données : l'âge lunaire de la mère au moment de la conception (de 18 à 45 ans) et le mois lunaire de la conception.",
      ],
    },
    {
      heading: "L'âge et le mois lunaires",
      paragraphs: [
        "En Chine, on a 1 an à la naissance, puis on prend une année à chaque Nouvel An chinois, et non à son anniversaire. L'âge lunaire a donc un ou deux ans de plus que l'âge habituel.",
        "Les mois lunaires commencent à chaque nouvelle lune, et certaines années en comptent treize. Le calcul s'en charge : pas besoin de convertir à la main.",
      ],
    },
    {
      heading: "Est-ce fiable ?",
      paragraphs: [
        "Non. Une étude suédoise portant sur 2,8 millions de naissances (Villamor et Cnattingius, 2010) a trouvé une prédiction juste une fois sur deux, exactement comme à pile ou face.",
        "Pour connaître le sexe de votre bébé, il faut attendre l'échographie du 2e trimestre, vers 22 SA, ou un test sanguin prescrit par votre médecin.",
      ],
    },
    {
      heading: "Sans date de conception ?",
      paragraphs: [
        "Indiquez le premier jour de vos dernières règles : la conception est estimée 14 jours plus tard, pour un cycle de 28 jours. Si elle tombe près d'un changement de mois lunaire, regardez aussi la case voisine du tableau.",
      ],
    },
  ],
  faq: [
    {
      question: "Le calendrier chinois prédit-il vraiment le sexe du bébé ?",
      answer:
        "Non : sa prédiction est juste une fois sur deux, comme le hasard. C'est une tradition amusante, pas une méthode médicale.",
    },
    {
      question: "Comment calculer son âge lunaire ?",
      answer:
        "C'est l'année lunaire de la conception moins l'année lunaire de votre naissance, plus un. Une année lunaire commence au Nouvel An chinois, entre le 21 janvier et le 20 février.",
    },
    {
      question: "Quand peut-on connaître le sexe du bébé ?",
      answer:
        "À l'échographie du 2e trimestre, vers 22 SA, si le bébé se laisse voir. Un test sanguin peut le révéler plus tôt, lorsqu'il est prescrit pour une raison médicale.",
    },
  ],
};

export function ChineseCalendarView() {
  return (
    <ToolLayout
      copy={CHINESE_CALENDAR_COPY}
      eyebrow="Pour s'amuser"
      stickers={[stickerUrl("bebe", "lune"), stickerUrl("bebe", "chaussons")]}
      after={<ChartTable />}
      disclaimer="Le calendrier chinois n'a aucune valeur médicale : sa prédiction est juste une fois sur deux."
    >
      <Predictor />
    </ToolLayout>
  );
}

type From = "conception" | "lmp" | "due";

const FROM: { mode: From; label: string; field: string }[] = [
  { mode: "conception", label: "Conception", field: "Date de conception" },
  { mode: "lmp", label: "Dernières règles", field: "Premier jour des dernières règles" },
  { mode: "due", label: "Terme", field: "Date prévue d'accouchement" },
];

/** The conception day each input implies: 14 days after the period, 9 months before term. */
const conceptionFrom = (from: From, date: string) =>
  from === "lmp" ? addDays(date, 14) : from === "due" ? addMonths(date, -9) : date;

function Predictor() {
  const [birth, setBirth] = useState("");
  const [date, setDate] = useState("");
  const [from, setFrom] = useState<From>("conception");
  const conception = ISO_DAY.test(date) ? conceptionFrom(from, date) : null;
  const result = ISO_DAY.test(birth) && conception ? predictGender(birth, conception) : null;
  const current = FROM.find((entry) => entry.mode === from) ?? FROM[0];
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="label self-end">
          Votre date de naissance
          <input
            type="date"
            className="input"
            value={birth}
            onChange={(event) => setBirth(event.target.value)}
          />
        </label>
        <div className="grid gap-1.5">
          <div className="flex flex-wrap gap-1">
            {FROM.map((entry) => (
              <button
                key={entry.mode}
                type="button"
                className={from === entry.mode ? "btn btn-sm" : "btn-ghost btn-sm"}
                onClick={() => setFrom(entry.mode)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <input
            type="date"
            className="input"
            aria-label={current?.field}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>
      </div>
      {result ? (
        <Prediction result={result} />
      ) : (
        <p className="text-center text-muted">
          La prédiction s'affiche dès les deux dates saisies.
        </p>
      )}
      <p className="text-center text-sm">
        Vous ne connaissez pas la date de conception ?{" "}
        <Link to="/calcul-date-de-conception" className="font-bold text-accent-text underline">
          Estimez-la ici
        </Link>
      </p>
    </>
  );
}

function Prediction({ result }: { result: GenderPrediction }) {
  const tone = result.prediction ? TONE[result.prediction] : null;
  return (
    <div
      className={`grid justify-items-center gap-2 rounded-[1.75rem] p-6 text-center ${tone?.cell ?? "bg-page"}`}
      aria-live="polite"
    >
      {tone ? (
        <>
          <p className="font-bold text-ink/70">Le calendrier chinois prédit</p>
          <p className={`font-soft text-5xl font-medium sm:text-6xl ${tone.text}`}>
            {result.prediction === "girl" ? "Une fille" : "Un garçon"}
          </p>
        </>
      ) : (
        <p className="font-soft text-2xl font-medium">
          Le tableau ne couvre que les âges lunaires de 18 à 45 ans.
        </p>
      )}
      <p className="text-ink/80">
        Âge lunaire : <strong>{result.lunarAge} ans</strong> · Mois lunaire de conception :{" "}
        <strong>
          {result.lunarMonth}
          {result.leapMonth && " (mois intercalaire)"}
        </strong>
      </p>
    </div>
  );
}

/** The whole chart, in the page's HTML: readable without the tool, and by crawlers. */
function ChartTable() {
  return (
    <section className="grid gap-4">
      <div className="grid gap-2">
        <h2 className="text-3xl font-semibold">Le tableau complet</h2>
        <p className="text-ink/80">
          En ligne, l'âge lunaire de la mère ; en colonne, le mois lunaire de conception.
        </p>
        <p className="flex gap-4 text-sm font-bold">
          <span className="flex items-center gap-1.5">
            <span className={`size-3 rounded-full ${TONE.girl.cell}`} /> Fille (F)
          </span>
          <span className="flex items-center gap-1.5">
            <span className={`size-3 rounded-full ${TONE.boy.cell}`} /> Garçon (G)
          </span>
        </p>
      </div>
      <div className="panel overflow-x-auto p-2 sm:p-4">
        <table className="w-full border-separate border-spacing-1 text-center text-sm">
          <caption className="sr-only">
            Calendrier chinois de grossesse : âge lunaire de la mère et mois lunaire de conception
          </caption>
          <thead>
            <tr>
              <th scope="col" className="px-1 text-left text-xs text-muted">
                Âge
              </th>
              {MONTHS.map((month) => (
                <th key={month} scope="col" className="text-xs text-muted">
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {AGES.map((age) => (
              <tr key={age}>
                <th scope="row" className="px-1 text-left font-bold">
                  {age}
                </th>
                {MONTHS.map((month) => {
                  const cell = chartCell(age, month);
                  return (
                    <td
                      key={month}
                      className={`h-7 min-w-7 rounded-md font-bold ${cell ? TONE[cell].cell : ""}`}
                    >
                      {cell === "girl" ? "F" : "G"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
